import { useEffect } from "react";
import ReactPlayer from "react-player";
import {
  Switch,
  Flex,
  Button,
  ConfigProvider,
  Form,
  Checkbox,
  InputNumber,
} from "antd";
import CardThemePalette from "../CardThemePalette";

import StyledContainer from "./style";

import { LIST_THEME } from "../../constant";

import {
  cleanPercentValue,
  getPercentValue,
  selectValue,
} from "../../../../../../../../../../utils/helper/general";

import { ReactComponent as LogoLevelWealth } from "../../../../image/logo_levelWealth.svg";

const SectionCompanySettings = ({
  onClickUpdateFeesTaxesInflation,
  onClickUpdateLogo,
  onClickUpdateTheme,
  onClickUpdateVideo,
  settings,
  showSectionFeesTaxesInflation,
  toggleFeature,
  toggleDashboardVideo,
  toggleFeesTaxesInflation,
  toggleTheme,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(settings?.financialFactors?.data);
  }, [settings]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorPrimary: "#019A00",
          },
          Switch: {
            colorPrimary: "#019A00",
            colorPrimaryHover: "#019A00",
          },
        },
      }}
    >
      <StyledContainer>
        {showSectionFeesTaxesInflation && (
          <article>
            <h4>Fees, Taxes & Inflation</h4>

            <div className="container-content">
              <header>
                <h5>Global Advisor Settings</h5>
                <Flex align="center" gap={8}>
                  <Switch
                    checked={settings?.financialFactors?.enabled}
                    onClick={toggleFeesTaxesInflation}
                  />
                  Enabled
                </Flex>
              </header>

              <Flex gap={43} justify="space-between">
                <Form
                  disabled
                  form={form}
                  layout="vertical"
                  variant="underlined"
                >
                  <Flex align="center" gap={24} vertical>
                    <Form.Item
                      extra={
                        <Form.Item
                          name="allowOverrideAdvisorFee"
                          valuePropName="checked"
                        >
                          <Checkbox>Allow advisors to override</Checkbox>
                        </Form.Item>
                      }
                      label="Global Advisor Fee"
                      name="globalAdvisorFee"
                    >
                      <InputNumber
                        formatter={getPercentValue}
                        max={20}
                        min={0}
                        onClick={selectValue}
                        parser={cleanPercentValue}
                        precision={2}
                        size="small"
                        step={0.1}
                      />
                    </Form.Item>

                    <Form.Item
                      extra={
                        <Form.Item
                          name="allowOverrideTax"
                          valuePropName="checked"
                        >
                          <Checkbox disabled>
                            Allow advisors to override
                          </Checkbox>
                        </Form.Item>
                      }
                      label="Global Tax"
                      name="globalTax"
                    >
                      <InputNumber
                        disabled
                        formatter={getPercentValue}
                        max={100}
                        min={0}
                        onClick={selectValue}
                        parser={cleanPercentValue}
                        precision={2}
                        size="small"
                        step={0.1}
                      />
                    </Form.Item>

                    <Form.Item
                      extra={
                        <Form.Item
                          name="allowOverrideInflation"
                          valuePropName="checked"
                        >
                          <Checkbox>Allow advisors to override</Checkbox>
                        </Form.Item>
                      }
                      label="Global Inflation"
                      name="globalInflation"
                    >
                      <InputNumber
                        formatter={getPercentValue}
                        max={100}
                        min={0}
                        onClick={selectValue}
                        parser={cleanPercentValue}
                        precision={2}
                        size="small"
                        step={0.1}
                      />
                    </Form.Item>
                  </Flex>
                </Form>

                <Button onClick={onClickUpdateFeesTaxesInflation} shape="round">
                  Update
                </Button>
              </Flex>
            </div>
          </article>
        )}

        <article>
          <h4>Features</h4>

          <div className="container-content">
            <Flex align="center" gap={8}>
              <Switch
                checked={
                  settings?.features?.find(it => it.name === "penny")?.enabled
                }
                onClick={() => toggleFeature("penny")}
              />
              <span>
                Penny{" "}
                <i>(This will apply to all advisors tied to this company)</i>
              </span>
            </Flex>
            <Flex align="center" gap={8}>
              <Switch
                checked={
                  settings?.features?.find(it => it.name === "modelBuilder")
                    ?.enabled ?? true
                }
                onClick={() => toggleFeature("modelBuilder")}
              />
              Model Builder
            </Flex>
          </div>
        </article>

        <article>
          <h4>Advisor Dashboard</h4>

          <div className="container-content">
            <header>
              <h5>Dashboard Video</h5>
              <Flex align="center" gap={8}>
                <Switch
                  checked={settings?.video?.enabled}
                  onClick={toggleDashboardVideo}
                />
                Enabled
              </Flex>
            </header>

            <Flex gap={43} justify="space-between">
              <div>
                {settings?.video?.url && (
                  <ReactPlayer
                    controls={true}
                    height={195}
                    url={settings.video.url}
                    width="100%"
                  />
                )}
              </div>

              <Button onClick={onClickUpdateVideo} shape="round">
                Update Video
              </Button>
            </Flex>
          </div>
        </article>

        <article>
          <h4>Company Brand</h4>

          <div className="container-content">
            <header>
              <h5>Your Logo</h5>
              <span style={{ color: "#052C2B", opacity: ".5" }}>Required</span>
            </header>

            <Flex gap={43}>
              <Flex gap={10} vertical>
                <p style={{ margin: 0 }}>Current Logo</p>
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid #C9C8C8",
                    width: 413,
                  }}
                >
                  {settings?.logo?.url ? (
                    <img
                      alt="Company Logo"
                      src={settings.logo.url}
                      style={{ height: 42 }}
                    />
                  ) : (
                    <LogoLevelWealth />
                  )}
                </Flex>
              </Flex>

              <Button onClick={onClickUpdateLogo} shape="round">
                Update Logo
              </Button>
            </Flex>
          </div>
        </article>

        <article>
          <h4>Color Theme</h4>

          <div className="container-content">
            <header>
              <h5>Color Theme</h5>
              <Flex align="center" gap={8}>
                <Switch
                  checked={settings?.theme?.enabled}
                  onClick={toggleTheme}
                />
                Enabled
              </Flex>
            </header>

            <Flex justify="space-between">
              {settings?.theme?.data?.id && (
                <CardThemePalette
                  hideActiveButton
                  themeData={LIST_THEME.find(
                    it => it.id === settings.theme.data.id
                  )}
                />
              )}

              <Button onClick={onClickUpdateTheme} shape="round">
                Update Colors
              </Button>
            </Flex>
          </div>
        </article>
      </StyledContainer>
    </ConfigProvider>
  );
};

export default SectionCompanySettings;
